import { html, TemplateResult, PropertyValueMap } from 'lit'
import type { CSSResultGroup } from 'lit'
import { property } from 'lit/decorators.js'
import { state } from 'lit/decorators/state.js'
import '@pushengineering/push-web-components/box'
import '@pushengineering/push-web-components/button'
import '@pushengineering/push-web-components/button-icon'
import '@pushengineering/push-web-components/horizontal-stack'
import '@pushengineering/push-web-components/input'
import '@pushengineering/push-web-components/link'
import '@pushengineering/push-web-components/link-group'
import '@pushengineering/push-web-components/text'
import '@pushengineering/push-web-components/vertical-stack'
import type Input from '@pushengineering/push-web-components/input'
import Button from '@pushengineering/push-web-components/button'
import loginStyles from './login.styles'

import { MagicOptions } from '../../base/magic/MagicAPI.js'
import { Environment } from '../../base/n3xus/types.js'
import { LoginStateTypes } from './types.js'
import CirkayElement from '../../internal/CirkayElement.js'
import { LoginController } from '../../controllers/LoginController.js'
import { FanPassUser } from '../../controllers/types.js'

const EMAIL_ERROR_MESSAGE = 'Please enter a valid email address.'

export class Login extends CirkayElement {
    static styles: CSSResultGroup = [loginStyles]
    private loginController: LoginController | null = null
    /**
     * A web component for Fan Pass claim.
     * @fires fp-login-initialising - Dispatched when the component is initializing.
     * @fires fp-login-initialised - Dispatched when the component is initialized and ready for use.
     * @fires fp-login - Dispatched when logged in
     * @fires fp-logout - Dispatched when logged out
     */
    public isLoggedin: boolean = false
    public user: FanPassUser | null = null
    public loginInit: boolean = false
    /**
     * public API for the Fan Pass you wish to coonect to / currently not used.
     * If empty could be a gentric login to see all Fan Passes tey own and benfits in the future
     */
    @property({ type: String })
    apiKey?: string | undefined

    /** hide login  */
    @property({ type: Boolean })
    hidden: boolean = false

    /** Enabled the user of the Login to set the login to loading when required  */
    @property({ type: Boolean })
    loading: boolean = false

    /** Loading Text  */
    @property({ type: String })
    loadingText: string = 'Connecting...'

    /** Button label for email submit  */
    @property({ type: String })
    buttonLabel: string = 'Connect'

    /** Environment to use for the magic link  */
    @property({ type: String })
    environment: Environment = Environment.PRODUCTION

    /** When true logs out extra infomation  */
    @property({ type: Boolean })
    debugLogging: boolean = false

    /** After the login is complete  */
    @property({ type: Boolean })
    showLoaderAfterLogin: boolean = false

    /** Do you check wallet for Fan Pass?  */
    @property({ type: Boolean })
    checkWallet: boolean = false

    @property({ type: String })
    callerId: string = ''

    /** Current State of the login  */
    @state()
    _state: LoginStateTypes = LoginStateTypes.initialising

    /** Login email  */
    @state()
    _email: string = ''

    /** DID Token from Magic when login  */
    @state()
    _did: string | null = null

    /** Wallet from Magic  */
    @state()
    _wallet: string | null = null

    /** User's OTP code entered  */
    @state()
    _otpCode: Array<string> = new Array(6).fill('')

    /** OTP code error  */
    @state()
    _otpCodeError: string | undefined = undefined

    /** Email validation error  */
    @state()
    _emailError: string | undefined = undefined

    constructor() {
        super()
    }

    /**
     * connectedCallback - LITS connectedCallback lifecycle method used to setup magic api and set current state
     *
     * @return  {void}
     */
    public connectedCallback(): void {
        super.connectedCallback()
        /*
        this.emit(LoginEvents.Initialising, {
            bubbles: true,
            composed: true,
        })
        */
        this.updateDefaultScreenVisibility()
        this.logging('connectedCallback')
        const magicApiOptions: MagicOptions = {
            environment: this.environment,
        }
        this.logging('magicApiOptions', magicApiOptions)
        this.loginController = LoginController.getInstance(
            magicApiOptions,
            this.debugLogging
        )
        this.loginController.addHost(this)
    }

    public disconnectedCallback() {
        super.disconnectedCallback()
        this.loginController?.removeHost(this) // Remove this element as a host
    }

    public updated(
        changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>
    ): void {
        // ensures error screen takes priority if error slot is filled
        this.updateDefaultScreenVisibility()
        changedProperties.forEach((oldValue, propName) => {
            switch (String(propName)) {
                case 'showLoaderAfterLogin':
                    if (
                        this._state === LoginStateTypes.isLoggedInLoading &&
                        !this.showLoaderAfterLogin
                    ) {
                        this._state = LoginStateTypes.isLoggedIn
                        this.loading = false
                    }
                    break
                case 'loading':
                    if (
                        this._state === LoginStateTypes.isLoggedInLoading &&
                        !this.loading
                    ) {
                        this._state = LoginStateTypes.isLoggedIn
                        this.loading = false
                    }
                    break
            }
            this.logging(`${String(propName)} changed. oldValue: ${oldValue}`)
        })
    }

    /**
     * getCurrentState - return the current state of the login
     *
     * @return  {LoginStateTypes}
     */
    public getCurrentState(): LoginStateTypes {
        return this._state
    }

    public async logout(): Promise<boolean> {
        if (!this.loginController) return false
        return await this.loginController?.logout()
    }

    public setLoading(loading: boolean): boolean {
        this.loading = loading
        return this.loading
    }

    private logging(message?: any, ...optionalParams: any[]): void {
        if (this.debugLogging) {
            console.log(message, optionalParams)
        }
    }

    /**
     * Updates the component's state based on changes in properties and the login controller.
     *
     * @param changedProperties - The properties that have changed.
     *
     * This method performs the following actions:
     * - Calls the superclass's update method.
     * - Synchronizes the component's state with the login controller's state.
     * - Updates the component's internal state based on the login state and other conditions.
     *
     * The state transitions handled include:
     * - Transitioning to `LoginStateTypes.initialised` if the user is not logged in.
     * - Transitioning to `LoginStateTypes.isLoggedInLoading` or `LoginStateTypes.isLoggedIn` based on login initialization and loader visibility.
     * - Transitioning to `LoginStateTypes.enterLoginCode` if waiting for OTP or OTP fails.
     * - Handling OTP submission errors and resetting the OTP code.
     */
    protected willUpdate(): void {
        if (this.loginController) {
            
            if (this.loginInit !== this.loginController.loginInit) {
                this.loginInit = this.loginController.loginInit
            }
            if (this.user !== this.loginController.user) {
                this.user = this.loginController.user
            }
            if (this.isLoggedin !== this.loginController.loggedIn) {
                this.isLoggedin = this.loginController.loggedIn

            }
        }

        if (this._state === LoginStateTypes.isLoggedIn && !this.isLoggedin) {
            this._otpCodeError = undefined
            this._state = LoginStateTypes.initialised

            if (this.checkWallet) {
            }
        }

        if (
            this._state === LoginStateTypes.initialising &&
            this.isLoggedin &&
            this.loginInit &&
            this.showLoaderAfterLogin
        ) {
            this._state = LoginStateTypes.isLoggedInLoading
        } else if (
            this._state === LoginStateTypes.initialising &&
            this.isLoggedin &&
            this.loginInit &&
            !this.showLoaderAfterLogin
        ) {
            this._state = LoginStateTypes.isLoggedIn
        } else if (
            this._state === LoginStateTypes.initialising &&
            !this.isLoggedin &&
            this.loginInit
        ) {
            this._state = LoginStateTypes.initialised
        }

        if (
            this._state === LoginStateTypes.optCodeSubmited &&
            this.isLoggedin &&
            this.showLoaderAfterLogin
        ) {
            this._state = LoginStateTypes.isLoggedInLoading
        } else if (
            this._state === LoginStateTypes.optCodeSubmited &&
            this.isLoggedin &&
            !this.showLoaderAfterLogin
        ) {
            this._state = LoginStateTypes.isLoggedIn
        }

        if (
            this._state === LoginStateTypes.requestCode &&
            this.loginController?.waitingForOtp &&
            !this.isLoggedin
        ) {
            this._state = LoginStateTypes.enterLoginCode
        }

        if (
            this._state === LoginStateTypes.requestCode &&
            this.loginController?.otpFail &&
            !this.isLoggedin
        ) {
            this._state = LoginStateTypes.optFailed
        }

        if (
            this._state === LoginStateTypes.optCodeSubmited &&
            !this.isLoggedin &&
            this.loginController?.otpError != null
        ) {
            this._otpCodeError = this.loginController?.otpError
            this._state = LoginStateTypes.enterLoginCode
            this.resetOptCode()
        }
    }

    /**
     * Updates the visibility of the default screen based on the presence of content
     * in the slot named "errorScreen". If the slot has assigned nodes, the default
     * screen will be hidden. Otherwise, the default screen will be shown.
     *
     * @private
     */
    private updateDefaultScreenVisibility() {
        const slot = this.shadowRoot?.querySelector(
            'slot[name="errorScreen"]'
        ) as HTMLSlotElement | null

        const defaultScreen = this.shadowRoot?.querySelector(
            '.default-screen'
        ) as HTMLElement | null

        const hasContent =
            slot && slot.assignedNodes({ flatten: true }).length > 0

        if (defaultScreen) {
            defaultScreen.classList.toggle('show-default-screen', !hasContent)
        }
    }

    /**
     * Validates an email address using a regular expression.
     *
     * @param email - The email address to validate.
     * @returns `true` if the email address is valid, `false` otherwise.
     */
    private validateEmail(email: string): boolean {
        const validRegex: RegExp =
            /([-!#-'*+/-9=?A-Z^-~]+(\.[-!#-'*+/-9=?A-Z^-~]+)*|"([]!#-[^-~ \t]|(\\[\t -~]))+")@([0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?(\.[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?)*|\[((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3}|IPv6:((((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){6}|::((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){5}|[0-9A-Fa-f]{0,4}::((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){4}|(((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):)?(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}))?::((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){3}|(((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){0,2}(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}))?::((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){2}|(((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){0,3}(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}))?::(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):|(((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){0,4}(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}))?::)((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3})|(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])(\.(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])){3})|(((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){0,5}(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}))?::(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3})|(((0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}):){0,6}(0|[1-9A-Fa-f][0-9A-Fa-f]{0,3}))?::)|(?!IPv6:)[0-9A-Za-z-]*[0-9A-Za-z]:[!-Z^-~]+)])/
        return email.match(validRegex) ? true : false
    }

    /**
     * Resets the OTP (One-Time Password) code input fields and submit button state.
     *
     * This method performs the following actions:
     * - Resets the internal `_otpCode` array to an array of 6 empty strings.
     * - Selects all input elements with the attribute `data-magic-code-input` within the shadow DOM and clears their values.
     * - Enables the input elements.
     * - Selects the submit button with the ID `optSubmitButton` within the shadow DOM.
     * - Sets the submit button's `loading` state to `false`.
     * - Disables the submit button.
     *
     * @private
     */
    private resetOptCode(): void {
        this._otpCode = new Array(6).fill('')
        const inputs = this.shadowRoot?.querySelectorAll(
            'pu-input[data-magic-code-input]'
        )
        if (inputs) {
            for (let i = 0; i < inputs.length; i++) {
                if (inputs) {
                    const input = inputs[i] as Input
                    input.value = ''
                    input.disabled = false
                }
            }
        }
        const sumbit = this.shadowRoot?.getElementById('optSubmitButton') as
            | Button
            | undefined
        if (sumbit) {
            sumbit.loading = false
            sumbit.disabled = true
        }
    }

    /**
     * Resets the OTP (One-Time Password) handler.
     *
     * This method performs the following actions:
     * - Clears the email field.
     * - Resets the OTP code.
     * - Sets the login state to the initialised state.
     *
     * @private
     */
    private resetOptHandler(): void {
        this._email = ''
        this.loginController?.cancelOtpHandle()
        this.resetOptCode()
        this._state = LoginStateTypes.initialised
    }

    /**
     * Handles the submission of the OTP (One-Time Password) code.
     *
     * This method prevents the default form submission behavior and stops the event propagation.
     * It then retrieves the OTP code from the `_otpCode` array, trims any whitespace, and checks if the code length is 6.
     * If the code is valid and `loginController` is defined, it calls the `optCodeInput` method on the `loginController`
     * with the OTP code and updates the state to `optCodeSubmited`.
     *
     * @param {Event} e - The event object from the form submission.
     */
    private async handleOtpCodeSubmit(e: Event): Promise<void> {
        e.preventDefault()
        e.stopPropagation()
        const otpCode: string = this._otpCode.join('').trim()
        if (otpCode.length === 6 && this.loginController) {
            this._state = LoginStateTypes.optCodeSubmited
            await this.loginController.optCodeInput(otpCode)
        }
    }

    /**
     * Handles the input event for OTP (One-Time Password) code input fields.
     * Ensures that only one digit is allowed per input field, updates the OTP code array,
     * and moves the focus to the next input field if the current input is not empty.
     *
     * @param e - The input event triggered by the user.
     * @param index - The index of the current OTP input field.
     */
    private handleOtpCodeInput(e: Event, index: number) {
        const input = e.target as HTMLInputElement
        const value = input.value

        // If more than one character is entered (e.g. mobile auto-fill), split it into individual digits.
        if (value.length > 1) {
            const digits = value.split('').slice(0, 6)
            digits.forEach((digit, i) => {
                this._otpCode[i] = digit
                const currentInputComponent = this.shadowRoot?.querySelectorAll(
                    'pu-input[data-magic-code-input]'
                )[i] as Input
                const currentInputField =
                    currentInputComponent.shadowRoot?.querySelector('input')
                // Assuming each pu-input has its own shadowRoot with an input element.
                if (currentInputField) {
                    currentInputField.value = digit
                }
            })
            // Focus the next available input (or the last one)
            const nextIndex = digits.length < 6 ? digits.length : 5
            const nextInput = this.shadowRoot?.querySelectorAll(
                'pu-input[data-magic-code-input]'
            )[nextIndex] as Input
            nextInput.shadowRoot?.querySelector('input')?.focus()
        } else {
            // When single digit entered - ensure only one digit is allowed
            if (value.length > 1) {
                input.value = value.slice(0, 1)
            }
            this._otpCode[index] = input.value

            // Move focus to the next input if current input is not empty
            if (input.value && index < 5) {
                const nextInput = this.shadowRoot?.querySelectorAll(
                    'pu-input[data-magic-code-input]'
                )[index + 1] as Input
                nextInput.shadowRoot?.querySelector('input')?.focus()
            }
        }

        this.checkOtpCodeValid()
    }

    /**
     * Handles the keydown event for OTP code input fields.
     *
     * @param e - The keyboard event triggered by the user.
     *
     * This function performs the following actions:
     * - Prevents non-numeric, non-control keys from being entered.
     * - Clears any existing OTP code error.
     * - Handles the backspace key to focus the previous input if the current input is empty.
     * - Handles the enter key to submit the OTP code if all fields are filled.
     *
     * The input fields are expected to have a `data-index` attribute indicating their position.
     * The function assumes that the input fields are part of a shadow DOM and have a class of `otpCodeInput`.
     */
    private handleOtpCodeKeyDown(e: KeyboardEvent) {
        const input = e.target as HTMLInputElement
        const index: number | undefined = input.dataset.index
            ? parseInt(input.dataset.index, 10)
            : undefined

        if (index === undefined) {
            //Error no index set
            return
        }

        if (
            isNaN(Number(e.key)) &&
            e.key !== 'Backspace' &&
            e.key !== 'Enter' &&
            !(e.ctrlKey || e.metaKey)
        ) {
            e.preventDefault()
        }
        this._otpCodeError = undefined
        // Handle backspace to focus the previous input
        if (e.key === 'Backspace' && !input.value && index > 0) {
            ;(
                this.shadowRoot!.querySelectorAll('.otp-code-input')[
                    index - 1
                ].shadowRoot!.querySelector('#input') as Input
            )?.focus()
        }

        //if (e.key === 'Enter' && this._otpCode.join('').trim().length === 6) {
        //this.handleOtpCodeSubmit(new Event('submit'))
        //}
    }

    /**
     * Checks if the OTP code is valid.
     *
     * This method verifies if the OTP code entered by the user is exactly 6 characters long.
     * If the OTP code is valid, it enables the submit button. Otherwise, it disables the submit button.
     *
     * @returns {boolean} - Returns `true` if the OTP code is valid (i.e., 6 characters long), otherwise returns `false`.
     */
    private checkOtpCodeValid(): boolean {
        const sumbitButton = this.shadowRoot?.getElementById(
            'optSubmitButton'
        ) as Button | undefined
        if (!sumbitButton) {
            return false
        }
        if (this._otpCode.join('').trim().length === 6) {
            sumbitButton.disabled = false

            return true
        }
        sumbitButton.disabled = true
        return false
    }

    /**
     * Resends the OTP (One-Time Password) code to the user's email.
     *
     * This method checks if the user's email is available. If it is, it clears any existing OTP code error
     * and calls the `resendOtpCode` method on the `loginController` with the user's email.
     *
     * @private
     */
    private resendOtpCode(): void {
        if (this._email) {
            this._otpCodeError = undefined
            this.loginController?.resendOtpCode(this._email)
        }
    }

    /**
     * Handles the paste event for the OTP (One-Time Password) code input.
     *
     * This method prevents the default paste action, validates the pasted data to ensure
     * it is a 6-digit numeric string, and then populates the OTP input fields with the
     * pasted digits. If the pasted data is valid and complete, it triggers the OTP code
     * submission.
     *
     * @param e - The ClipboardEvent triggered by the paste action.
     */
    private handleOtpCodePaste(e: ClipboardEvent) {
        e.preventDefault()
        const pasteData = e.clipboardData?.getData('text') || ''
        const re = new RegExp('^[0-9]{6}$')
        if (!re.test(pasteData)) {
            return
        }
        const digits = pasteData.slice(0, 6).split('')
        digits.forEach((digit, index) => {
            if (index < this._otpCode.length) {
                this._otpCode[index] = digit
                const input = this.shadowRoot!.querySelectorAll(
                    'pu-input[data-magic-code-input]'
                )[index] as HTMLInputElement
                input.value = digit
            }
        })

        // Focus the last populated input
        const nextIndex = digits.length < 6 ? digits.length : 5
        ;(
            this.shadowRoot!.querySelectorAll('input')[
                nextIndex
            ] as HTMLInputElement
        )?.focus()

        const trimmedOtpCode: string = this._otpCode.join('').trim()
        if (trimmedOtpCode.length === 6) {
            this.handleOtpCodeSubmit(new Event('submit'))
        }
    }

    /**
     * Handles the input event for the email field.
     *
     * This method is triggered when the user types into the email input field.
     * It updates the internal `_email` property with the current value of the input.
     * If the email is valid, it clears any existing email error.
     *
     * @param {Event} e - The input event triggered by the email input field.
     */
    private emailInputHandler(e: Event): void {
        if (e.target) {
            const emailInput = e.target as Input
            const value = emailInput.value
            this._email = value

            if (this.validateEmail(value)) {
                this._emailError = undefined
            }
        }
    }

    /**
     * Handles the submission of the email form.
     *
     * This method prevents the default form submission behavior and stops the immediate propagation of the event.
     * It retrieves the email input element from the shadow DOM and validates the email address.
     * If the email is invalid, it sets an error message and updates the state to `initialised`.
     * If the email is valid, it initializes the OTP handle using the `loginController` and updates the state to `requestCode`.
     *
     * @param {Event} e - The event object associated with the form submission.
     * @private
     */
    private submitEmail(e: Event): void {
        e.preventDefault()
        e.stopImmediatePropagation()

        /**
         * Recursively finds all form elements (input, select, textarea)
         * within an element, traversing through Shadow DOM and slots.
         * @param {Element} root The element to start searching from.
         * @returns {HTMLElement[]} An array of found form elements.
         */
        const findFormElementsRecursively = (
            root: Element
        ): HTMLElement[] => {
            const results: HTMLElement[] = []
            const visited = new Set<Element>()

            const traverse = (element: Element) => {
                if (!element || visited.has(element)) {
                    return
                }
                visited.add(element)

                // Check if the current element is a form element
                if (element.matches('input, select, textarea')) {
                    results.push(element as HTMLElement)
                }

                // Traverse into the Shadow DOM if it exists
                if (element.shadowRoot) {
                    Array.from(element.shadowRoot.children).forEach(traverse)
                }

                // Traverse into slotted content or light DOM children
                if (element.tagName === 'SLOT') {
                    ;(element as HTMLSlotElement)
                        .assignedElements({ flatten: true })
                        .forEach(traverse)
                } else {
                    Array.from(element.children).forEach(traverse)
                }
            }

            traverse(root)
            return results
        }

        // Get the host component that contains this fp-login instance.
        // This should be the <fp-claim> element.
        const hostComponent = (this.getRootNode() as ShadowRoot)?.host as HTMLElement
        let validForm = true
        if (hostComponent) {
            // Now, search for all form elements within the host component.
            const formElements = findFormElementsRecursively(hostComponent)

            const formData = new FormData(e.target as HTMLFormElement)
            const signUpData: { [key: string]: string } = {}
            if (formElements.length > 0) {
                formElements.forEach((el: any) => {
                    const htmlInput = el as HTMLInputElement
                    // Only include checked checkboxes/radio buttons
                    if (
                        (htmlInput.type === 'checkbox' ||
                            htmlInput.type === 'radio') &&
                        !htmlInput.checked
                    ) {
                        return
                    }

                    if (!htmlInput.checkValidity()) {
                        validForm = false
                    }
                    if (htmlInput.name) {
                        const value =
                            htmlInput.type === 'checkbox'
                                ? String(htmlInput.checked)
                                : htmlInput.value
                        signUpData[htmlInput.name] = value
                        formData.append(htmlInput.name, value)
                    }
                })
            }
            this.loginController?.setOptinDetails(signUpData)
        }

        if (!this.validateEmail(this._email) || !validForm) {
            this._emailError = EMAIL_ERROR_MESSAGE
            this._state = LoginStateTypes.initialised
            return
        }
        if (this.loginController) {
            this.loginController.initOtpHandle(
                this._email,
                (this.callerId.length > 0 ? this.callerId : null)
            )
            this._state = LoginStateTypes.requestCode
        }
    }

    /**
     * Renders the OTP code error message.
     *
     * This method returns a TemplateResult containing the error message
     * and a button to resend the OTP code if the maximum retry count is exceeded.
     *
     * @returns {TemplateResult} The HTML template for the OTP code error message.
     */
    private renderOtpCodeError(): TemplateResult {
        if (this._otpCodeError !== undefined) {
            return html`
                <pu-vertical-stack
                    exportparts="base:otp-code-error-layout"
                    part="base-otp-code-error-layout"
                    class="otp-code-error"
                    spacing="4"
                    align="center"
                    alignNarrow="center"
                >
                    <pu-text
                        as="p"
                        variant="bodySm"
                        withoutMargin
                        >
                        ${this._otpCodeError}
                    </pu-text>

                    ${this.loginController?.otpCodeRetriesCount &&
                    this.loginController?.otpCodeRetriesCount > 2
                        ? html`
                              <pu-button
                                  exportparts="button:button-link,button:resend-otp-link"
                                  part="base-button-link base-resend-otp-link"
                                  @click=${this.resendOtpCode}
                                  class="resend-otp-code-button"
                                  type="button"
                                  variant="plain-underline"
                              >
                                  <pu-text
                                      exportparts="base:button-link-label,base:resend-otp-link-label"
                                      part="base-button-link-label base-resend-otp-link-label"
                                      as="span"
                                      variant="bodySm"
                                      withoutMargin
                                  >
                                      Resend code?
                                  </pu-text>
                              </pu-button>
                          `
                        : html``}
                </pu-vertical-stack>
            `
        }
        return html``
    }

    /**
     * Renders a generic loading component.
     *
     * @returns {TemplateResult} The HTML template for the loading component.
     */
    private renderGenericLoader(): TemplateResult {
        return html`
            <pu-vertical-stack
                class="loading"
                spacing="32"
                spacingNarrow="24"
                align="center"
                alignNarrow="center"
                justify="center"
                justifyNarrow="center"
            >
                <pu-text as="p" variant="bodyLg" withoutMargin>
                    ${this.loadingText}
                </pu-text>

                <svg
                    part="loading-spinner"
                    class="loading__spinner"
                    width="16"
                    height="16"
                    xmlns="http://www.w3.org/2000/svg"
                    focusable="false"
                    role="presentation"
                    aria-hidden="true"
                    viewBox="0 0 512 512"
                >
                    <path
                        d="M463.9 376c7.6 4.4 17.5 1.8 21.4-6.1c17.1-34.3 26.7-73 26.7-113.9C512 120 405.9 8.8 272 .5c-8.8-.5-16 6.7-16 15.5v0c0 8.8 7.2 15.9 16 16.6C388.2 40.8 480 137.7 480 256c0 35.1-8.1 68.3-22.5 97.9c-3.9 8-1.3 17.7 6.4 22.2v0z"
                    />
                </svg>
            </pu-vertical-stack>
        `
    }

    /**
     * Renders the logged-in state of the component.
     *
     * This method returns a TemplateResult containing:
     * - A message indicating the user is connected.
     * - The email address used by the logged-in user.
     * - A button to disconnect the user.
     *
     * @returns {TemplateResult} The template result for the logged-in state.
     */
    private renderLoggedIn(): TemplateResult {
        return html`
            <pu-text as="p" variant="bodyLg" withoutMargin>
                You are connected!
            </pu-text>
            <pu-text as="p" variant="bodyMd" withoutMargin>
                Email used: ${this.user?.email}
            </pu-text>
            <pu-button
                exportparts="button,button:disconnect-button"
                part="base-button base-disconnect-button"
                @click="${this.logout}"
                type="button"
                class="button"
                name="disconnect"
                size="medium"
                variant="solid"
            >
                <pu-text
                    exportparts="base:button-label,base:disconnect-button-label"
                    part="base-button-label base-disconnect-button-label"
                    as="span"
                    variant="bodyMd"
                    fontWeight="regular"
                    withoutMargin
                    spacing="loose"
                >
                    Disconnect
                </pu-text>
            </pu-button>
        `
    }

    /**
     * Renders the email input form.
     *
     * @returns {TemplateResult} The HTML template for the email input form.
     *
     * The form includes:
     * - A vertical stack container with spacing and alignment properties.
     * - A text element to display email error messages.
     * - An input field for the email address with various properties such as type, label, placeholder, and event handlers.
     * - A submit button with properties such as size, style, loading state, and disabled state.
     *
     * The form submission and input events are handled by `submitEmail`, `emailInputHandler`, and `emailKeydownHandler` methods.
     * The button label is dynamically set based on the `buttonLabel` property.
     * The input and button states are controlled by the `_state` and `_email` properties.
     */
    private renderEmailInput(): TemplateResult {
        return html`
            <form
                part="login-email-form"
                id="emailInputForm"
                class="form"
                @submit="${this.submitEmail}"
            >
                <pu-vertical-stack
                    exportparts="base:login-layout"
                    part="base-login-layout"
                    class="login-email-layout"
                    align="center"
                    alignNarrow="center"
                >
                    <pu-text
                        class="email-error"
                        as="p"
                        variant="bodySm"
                        withoutMargin
                    >
                        ${this._emailError}
                    </pu-text>
                    <pu-input
                        exportparts="input:login-input"
                        part="base-login-input base-input"
                        class="email-input"
                        type="email"
                        label="Email address"
                        labelHidden
                        id="emailInput"
                        form="emailInputForm"
                        size="large"
                        type="email"
                        positionlabel="above"
                        autocomplete="email"
                        inputmode="email"
                        name="email"
                        placeholder="Email address"
                        autofocus
                        ?disabled=${this._state === LoginStateTypes.requestCode}
                        @pu-input=${this.emailInputHandler}
                    >
                    </pu-input>
                    <slot name="form-content-before-submit"></slot>
                    <pu-button
                        exportparts="button,button:login-button"
                        part="base-login-button base-button"
                        class="button"
                        type="submit"
                        form="emailInputForm"
                        id="emailSubmitButton"
                        name="emailSubmitButton"
                        for="emailInputForm"
                        size="large"
                        fullWidth
                        variant="solid"
                        ?loading=${this._state === LoginStateTypes.requestCode}
                        ?disabled=${this._email.length === 0 ||
                        this._state === LoginStateTypes.requestCode}
                    >
                        <pu-text
                            exportparts="base:button-label,base:login-button-label"
                            part="base-button-label base-login-button-label"
                            as="span"
                            variant="bodyMd"
                            fontWeight="regular"
                            withoutMargin
                            spacing="loose"
                        >
                            ${this.buttonLabel}
                        </pu-text>
                    </pu-button>
                    <slot name="form-content"></slot>
                </pu-vertical-stack>
            </form>
        `
    }

    /**
     * Renders the OTP (One-Time Password) code input form.
     *
     * This method generates the HTML template for the OTP code input form, which includes:
     * - A message prompting the user to enter the code sent to their email.
     * - A form with six input fields for the OTP code, each allowing only one character.
     * - A submit button that is disabled until all six characters are entered.
     * - An error message if the OTP code is incorrect.
     * - A button to restart the OTP process if the code is not received.
     *
     * @returns {TemplateResult} The HTML template for the OTP code input form.
     */
    private renderOtpCodeInput(): TemplateResult {
        return html`
            <div part="otp-form-container" class="formContainer">
                <pu-vertical-stack
                    exportparts="base:otp-layout"
                    part="base-otp-layout"
                    spacing="32"
                    spacingNarrow="24"
                    align="center"
                    alignNarrow="center"
                >
                    <pu-vertical-stack
                        exportparts="base:otp-email-layout"
                        part="base-otp-email-layout"
                        spacing="16"
                        spacingNarrow="8"
                        align="center"
                        alignNarrow="center"
                    >
                        <pu-text
                            exportparts="base:otp-email-text"
                            part="base-otp-email-text base-text base-body"
                            as="p"
                            variant="bodyLg"
                            withoutMargin
                            >
                            Please enter the code sent to:
                        </pu-text>
                        <pu-text
                            exportparts="base:otp-email"
                            part="base-otp-email base-text base-body"
                            as="p"
                            variant="bodyMd"
                            withoutMargin
                            >
                            ${this._email}
                        </pu-text>
                    </pu-vertical-stack>
                    <pu-vertical-stack
                        exportparts="base:otp-input-layout"
                        part="base-otp-input-layout"
                        spacing="24"
                        spacingNarrow="8"
                        align="center"
                        alignNarrow="center"
                    >
                        <form id="otpCodeForm" @submit="${this.handleOtpCodeSubmit}">
                            <pu-vertical-stack
                                exportparts="base:otp-form-layout"
                                part="base-otp-form-layout"
                                spacing="24"
                                spacingNarrow="16"
                                align="center"
                                alignNarrow="center"
                            >
                                <pu-vertical-stack
                                    spacing="16"
                                    align="center"
                                    alignNarrow="center"
                                >
                                    <pu-horizontal-stack>
                                        ${this._otpCode.map(
                                            (_, index) => html`
                                                <pu-input
                                                    class=${`otp-code-input ${this._otpCodeError ? 'otp-code-error' : ''}`}
                                                    size="large"
                                                    type="tel"
                                                    label=${(
                                                        index + 1
                                                    ).toString()}
                                                    labelHidden
                                                    data-magic-code-input
                                                    data-index=${index}
                                                    ?autofocus=${index === 0}
                                                    ?disabled=${this._state ===
                                                    LoginStateTypes.optCodeSubmited}
                                                    name="PasscodeInput${(
                                                        index + 1
                                                    ).toString()}"
                                                    id="PasscodeInput${(
                                                        index + 1
                                                    ).toString()}"
                                                    maxlength="1"
                                                    minlength="1"
                                                    inputmode="numeric"
                                                    autocomplete="one-time-code"
                                                    @input=${(
                                                        e: InputEvent
                                                    ) => {
                                                        this.handleOtpCodeInput(
                                                            e,
                                                            index
                                                        )
                                                    }}
                                                    @keydown=${this
                                                        .handleOtpCodeKeyDown}
                                                    @paste="${index === 0
                                                        ? this.handleOtpCodePaste.bind(
                                                                this
                                                            )
                                                        : null}"
                                                ></pu-input>
                                            `
                                        )}
                                    </pu-horizontal-stack>
                                    ${this.renderOtpCodeError()}
                                </pu-vertical-stack>
                                <pu-button
                                    exportparts="button,button:submit-otp-button"
                                    part="base-button base-submit-otp-button"
                                    id="optSubmitButton"
                                    type="submit"
                                    class="button"
                                    name="otpCodeSubmit"
                                    value=""
                                    form="otpCodeForm"
                                    for="otpCodeForm"
                                    size="medium"
                                    variant="solid"
                                    ?loading=${this._state ===
                                    LoginStateTypes.optCodeSubmited}
                                    fullWidth
                                    ?disabled=${this._state ===
                                        LoginStateTypes.optCodeSubmited ||
                                    this._otpCode.join('').trim().length !== 6}
                                >
                                    <pu-text
                                        exportparts="base:button-label,base:submit-otp-button-label"
                                        part="base-button-label base-submit-otp-button-label"
                                        as="span"
                                        variant="bodyMd"
                                        fontWeight="regular"
                                        withoutMargin
                                        spacing="loose"
                                    >
                                    Submit
                                    </pu-text>
                                </pu-button>
                            </pu-vertical-stack>
                        </form>
                        <pu-horizontal-stack
                            exportparts="base:form-resend-otp-layout"
                            part="base-form-resend-otp-layout"
                            spacing="24"
                        >
                            <pu-text
                                exportparts="base:form-resend-otp-text"
                                part="base-form-resend-otp-text"
                                as="span"
                                variant="bodySm"
                                withoutMargin
                                >
                                Haven't received your code?
                            </pu-text>
                            <pu-button
                                exportparts="button:button-link,button:resend-otp-link"
                                part="base-button-link base-resend-otp-link"
                                class="resend-otp-code-button"
                                type="button"
                                variant="plain-underline"
                                @click=${this.resetOptHandler}
                                >
                                <pu-text
                                    exportparts="base:button-link-label,base:resend-otp-link-label"
                                    part="base-button-link-label base-resend-otp-link-label"
                                    as="span"
                                    variant="bodySm"
                                    withoutMargin
                                >
                                    Restart
                                </pu-text>
                            </pu-button>
                        </pu-horizontal-stack>
                    </pu-vertical-stack>
                </pu-vertical-stack>
            </div>
        `
    }

    /**
     * Renders the OTP code failure message.
     *
     * This method returns a TemplateResult containing a message indicating that the user
     * has entered an incorrect email, along with a button to restart the OTP process.
     *
     * @returns {TemplateResult} The template result containing the OTP code failure message and restart button.
     */
    private renderOtpCodeFailed(): TemplateResult {
        return html`
            <div part="otp-code-failed-container">
                <pu-vertical-stack
                    spacing="32"
                    align="center"
                    alignNarrow="center"
                >
                    <pu-text as="p" variant="bodyLg" withoutMargin>
                        You have entered an incorrect email
                    </pu-text>
                    <pu-button
                        exportparts="button:button-link,button:resend-otp-link"
                        part="base-button-link base-resend-otp-link"
                        @click=${this.resetOptHandler}
                        class="resend-otp-code-button"
                        type="button"
                        variant="plain-underline"
                    >
                        <pu-text
                            exportparts="base:button-link-label,base:resend-otp-link-label"
                            part="base-button-link-label base-resend-otp-link-label"
                            as="span"
                            variant="bodySm"
                            withoutMargin
                        >
                            Restart
                        </pu-text>
                    </pu-button>
                </pu-vertical-stack>
            </div>
        `
    }

    /**
     * renderFlow - HTML render for login flow
     *
     * @return  {TemplateResult}
     */
    /**
     * Renders the login flow based on the current state.
     *
     * This function handles different states such as:
     * - initialised: Renders the email input form.
     * - requestCode: Renders the email input form.
     * - enterLoginCode: Renders the OTP code input form.
     * - optCodeSubmited: Renders the OTP code input form.
     * - optFailed: Renders the OTP code failed message.
     * - isLoggedIn: Renders the logged-in message.
     * - isLoggedInLoading: Renders the loading message.
     *
     * @return {TemplateResult} The rendered HTML template.
     */
    private renderFlow(): TemplateResult {
        const flowStages = () => {
            switch (this._state) {
                case LoginStateTypes.initialised:
                case LoginStateTypes.requestCode:
                    return html`${this.renderEmailInput()}`
                case LoginStateTypes.enterLoginCode:
                case LoginStateTypes.optCodeSubmited:
                    return html`${this.renderOtpCodeInput()}`
                case LoginStateTypes.optFailed:
                    return html`${this.renderOtpCodeFailed()}`
                case LoginStateTypes.isLoggedIn:
                    return html`${this.renderLoggedIn()}`
                case LoginStateTypes.isLoggedInLoading:
                    return html`${this.renderGenericLoader()}`
            }
            return html``
        }

        return html`
            <pu-vertical-stack
                exportparts="base:form-layout"
                part="base-form-layout"
                class="flow-container default-screen"
                spacing="32"
                align="center"
                alignNarrow="center"
            >
                ${flowStages()}
            </pu-vertical-stack>
        `
    }

    /**
     * Renders the inline content for the login component.
     *
     * This method generates a `TemplateResult` that conditionally displays
     * different content based on the current state of the login process.
     *
     * - If the state is `initialising`, it shows a loading message.
     * - Otherwise, it renders the flow content.
     *
     * @returns {TemplateResult} The rendered HTML template.
     */
    private renderInline(): TemplateResult {
        const content = () => {
            if (this._state === LoginStateTypes.initialising) {
                return html`
                    <pu-vertical-stack
                        class="loading default-screen"
                        spacing="32"
                        spacingNarrow="24"
                        align="center"
                        alignNarrow="center"
                        justify="center"
                        justifyNarrow="center"
                    >
                        <pu-text as="p" variant="bodyLg" withoutMargin>
                            Initialising...
                        </pu-text>
                        <svg
                            part="loading-spinner"
                            class="loading__spinner"
                            width="16"
                            height="16"
                            xmlns="http://www.w3.org/2000/svg"
                            focusable="false"
                            role="presentation"
                            aria-hidden="true"
                            viewBox="0 0 512 512"
                        >
                            <path
                                d="M463.9 376c7.6 4.4 17.5 1.8 21.4-6.1c17.1-34.3 26.7-73 26.7-113.9C512 120 405.9 8.8 272 .5c-8.8-.5-16 6.7-16 15.5v0c0 8.8 7.2 15.9 16 16.6C388.2 40.8 480 137.7 480 256c0 35.1-8.1 68.3-22.5 97.9c-3.9 8-1.3 17.7 6.4 22.2v0z"
                            />
                        </svg>
                    </pu-vertical-stack>
                `
            }

            return html`${this.renderFlow()}`
        }

        return html`
            <pu-box
                exportparts="base:flow-container,content:flow-content-layout"
                part="base-flow-container"
                class="flow-container"
                padding="0"
                paddingNarrow="0"
                >
                ${content()}
            </pu-box>
        `
    }

    /**
     * Renders the login component.
     *
     * This method logs the current state and returns a TemplateResult
     * that contains a div element. The div element's class is set to 'hidden'
     * if the `hidden` property is true, otherwise it is an empty string.
     * The content of the div is generated by the `renderInline` method.
     *
     * @returns {TemplateResult} The rendered template.
     */
    public render(): TemplateResult {
        this.logging('render / state', this._state)
        return html`
            <div part="connect-main" class=${this.hidden ? 'hidden' : ''}>
                ${this.renderInline()}
            </div>
        `
    }
}
